import { vec3 } from 'gl-matrix';
import { Ray } from '../../math/Ray';
import { HeightFieldHit, HeightFieldSample } from './HeightField';

/** Sampler callback: returns surface height at (x, z), or null if unsampleable. */
export type HeightSampler = (x: number, z: number) => number | null;

/** Sampler with normal: implementations supply central differences in their native units. */
export type HeightWithNormalSampler = (x: number, z: number) => HeightFieldSample | null;

const DEFAULT_MAX_DISTANCE = 5000;
const DEFAULT_BASE_STEP = 2;
const MAX_STEPS = 4096;
const BISECTION_ITERATIONS = 16;

/**
 * Adaptive ray-march against a heightfield sampler. Steps adaptively shrink as
 * the ray approaches the surface (gap-aware), bisects on the first crossing
 * step. Returns null when the ray never dips below the sampled surface within
 * `maxDistance`, when the ray points purely upward (no descent), or when the
 * sampler returns null.
 *
 * The `baseStep` controls the coarse step size — typically the heightfield's
 * native cell size or ~2 m for noise. Smaller = more accurate near grazing
 * angles, slower for long rays.
 */
export function raycastHeightField(
  ray: Ray,
  sample: HeightSampler,
  withNormal: HeightWithNormalSampler,
  maxDistance: number = DEFAULT_MAX_DISTANCE,
  baseStep: number = DEFAULT_BASE_STEP,
): HeightFieldHit | null {
  const ox = ray.origin[0];
  const oy = ray.origin[1];
  const oz = ray.origin[2];
  const dx = ray.direction[0];
  const dy = ray.direction[1];
  const dz = ray.direction[2];

  let t = 0;
  let prevT = 0;
  let prevDelta = (() => {
    const h0 = sample(ox, oz);
    if (h0 === null) return null;
    return oy - h0;
  })();

  // Ray origin already underground? Snap to entry.
  if (prevDelta !== null && prevDelta <= 0) {
    return resolveHit(ox, oy, oz, withNormal, t);
  }
  if (prevDelta === null) return null;

  for (let step = 0; step < MAX_STEPS && t < maxDistance; step++) {
    // Adaptive step: when the ray is high above the surface, take big strides.
    // When close, shrink. Floor at baseStep so we always make progress.
    const stride = Math.max(baseStep, prevDelta * 0.5);
    const nextT = Math.min(t + stride, maxDistance);
    const px = ox + dx * nextT;
    const py = oy + dy * nextT;
    const pz = oz + dz * nextT;
    const h = sample(px, pz);
    if (h === null) return null;
    const delta = py - h;

    if (delta <= 0) {
      // Crossing between t and nextT — bisect.
      let lo = t;
      let hi = nextT;
      let loDelta = prevDelta;
      let hiDelta = delta;
      for (let i = 0; i < BISECTION_ITERATIONS; i++) {
        // Linear-interp guess for the crossing — converges faster than midpoint
        // when one side has a big magnitude.
        const span = loDelta - hiDelta;
        const tau = span > 1e-6 ? loDelta / span : 0.5;
        const mid = lo + (hi - lo) * Math.min(0.95, Math.max(0.05, tau));
        const mx = ox + dx * mid;
        const my = oy + dy * mid;
        const mz = oz + dz * mid;
        const mh = sample(mx, mz);
        if (mh === null) break;
        const mDelta = my - mh;
        if (mDelta > 0) {
          lo = mid;
          loDelta = mDelta;
        } else {
          hi = mid;
          hiDelta = mDelta;
        }
        if (hi - lo < 1e-4) break;
      }
      const hitT = (lo + hi) * 0.5;
      const hx = ox + dx * hitT;
      const hy = oy + dy * hitT;
      const hz = oz + dz * hitT;
      return resolveHit(hx, hy, hz, withNormal, hitT);
    }

    prevT = t;
    void prevT;
    prevDelta = delta;
    t = nextT;
    if (t >= maxDistance) break;
  }

  return null;
}

function resolveHit(
  x: number,
  y: number,
  z: number,
  withNormal: HeightWithNormalSampler,
  distance: number,
): HeightFieldHit {
  const sample = withNormal(x, z);
  return {
    position: vec3.fromValues(x, y, z),
    normal: sample ? sample.normal : vec3.fromValues(0, 1, 0),
    distance,
  };
}

/**
 * Central-differences normal helper for any height sampler. Used by all HeightField
 * implementations as a default `sampleHeightWithNormal` body.
 */
export function centralDifferenceNormal(
  x: number,
  z: number,
  eps: number,
  sample: HeightSampler,
): HeightFieldSample | null {
  const h = sample(x, z);
  if (h === null) return null;
  const hx = sample(x + eps, z) ?? h;
  const hz = sample(x, z + eps) ?? h;
  const dx = (hx - h) / eps;
  const dz = (hz - h) / eps;
  const len = Math.sqrt(dx * dx + 1 + dz * dz);
  return {
    height: h,
    normal: vec3.fromValues(-dx / len, 1 / len, -dz / len),
  };
}
